import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, AlertCircle, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap, scrollToTarget } from "@/lib/motion";
import { trackShipment, normalizeTrackingInput, isValidTracking, TRACKING_LENGTH } from "@/lib/trackApi";
import TrackResult from "./TrackResult";

function writeUrl(number) {
  const { pathname } = window.location;
  const onTrackPage = pathname.startsWith("/track");
  const next = number ? (onTrackPage ? `/track/${number}` : `${pathname}?track=${number}`) : onTrackPage ? "/track" : pathname;
  window.history.replaceState(window.history.state, "", next);
}

/**
 * The public tracker. `initialNumber` (from /track/:number or ?track=) is looked
 * up once on mount. `headingAs` lets the /track page use it as the page's h1.
 */
export default function TrackingSection({ initialNumber = "", headingAs: Heading = "h2", id = "track" }) {
  const { t, lang } = useLang();
  const tr = t.track;
  const root = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const [value, setValue] = useState(() => normalizeTrackingInput(initialNumber));
  const [state, setState] = useState({ status: "idle" });

  const run = useCallback(async (number) => {
    const n = normalizeTrackingInput(number);
    if (!isValidTracking(n)) {
      setState({ status: "invalid" });
      inputRef.current?.focus();
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setState({ status: "loading" });
    try {
      const res = await trackShipment(n, { signal: ctrl.signal });
      if (res.status === "found") {
        setState({ status: "found", shipment: res.shipment });
        writeUrl(n);
      } else {
        setState({ status: res.status });
      }
    } catch (err) {
      if (err?.name !== "AbortError") setState({ status: "error" });
    }
  }, []);

  useEffect(() => {
    const n = normalizeTrackingInput(initialNumber);
    if (isValidTracking(n)) {
      run(n);
      if (window.location.pathname === "/") {
        setTimeout(() => scrollToTarget(`#${id}`, { immediate: true }), 60);
      }
    }
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    run(value);
  };

  const onReset = () => {
    setState({ status: "idle" });
    setValue("");
    writeUrl(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useGsap(
    () => {
      gsap.from(".trk-reveal", {
        y: 40,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        scrollTrigger: { trigger: root.current, start: "top 75%", once: true },
      });
    },
    [lang],
    root,
  );

  const loading = state.status === "loading";
  const errorKey = ["invalid", "not_found", "rate_limited", "error"].includes(state.status) ? state.status : null;
  const filled = value.length;

  return (
    <section
      ref={root}
      id={id}
      data-testid="tracking-section"
      aria-labelledby={`${id}-title`}
      className="on-dark relative z-raised bg-[var(--track-bg)] [--track-bg:oklch(0.145_0.014_140)] scroll-mt-20"
    >
      <div aria-hidden className="absolute inset-0 dot-grid opacity-40 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
      <div className="container-x relative py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-end">
          <div className="lg:col-span-6">
            <p className="trk-reveal flex items-center gap-2.5 text-[15px] font-semibold text-[var(--signal)]">
              <Search className="w-4 h-4" aria-hidden />
              {tr.kicker}
            </p>
            <Heading id={`${id}-title`} className="trk-reveal type-display mt-5 text-[clamp(2.2rem,5vw,4.25rem)] text-[var(--on-dark)]">
              {tr.title}
            </Heading>
          </div>
          <div className="lg:col-span-6">
            <p className="trk-reveal text-lg leading-relaxed text-[var(--on-dark-muted)] max-w-[48ch]">{tr.subtitle}</p>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate className="trk-reveal mt-12" data-testid="track-form">
          <label htmlFor={`${id}-input`} className="block text-sm font-semibold text-[var(--on-dark-muted)] mb-3">
            {tr.label}
          </label>
          <div className="relative flex flex-col sm:flex-row gap-3 rounded-[1.5rem] bg-[oklch(0.2_0.02_140)] border border-white/10 p-2.5 focus-within:border-[oklch(0.9_0.19_122/0.6)] transition-colors">
            <div className="relative flex-1 min-w-0" dir="ltr">
              <input
                ref={inputRef}
                id={`${id}-input`}
                data-testid="track-input"
                value={value}
                onChange={(e) => {
                  setValue(normalizeTrackingInput(e.target.value));
                  if (errorKey) setState({ status: "idle" });
                }}
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
                placeholder={tr.placeholder}
                aria-invalid={errorKey === "invalid" || undefined}
                aria-describedby={`${id}-digits ${id}-status`}
                className="w-full h-16 sm:h-[72px] bg-transparent px-4 sm:px-5 type-num type-wide text-[1.65rem] sm:text-[2.1rem] font-bold tracking-[0.16em] text-[var(--on-dark)] placeholder:text-white/20 outline-none focus-visible:outline-none"
              />
              {/* 10 ticks fill as digits are typed */}
              <div className="absolute bottom-1.5 inset-x-4 sm:inset-x-5 flex gap-1" aria-hidden>
                {Array.from({ length: TRACKING_LENGTH }, (_, i) => (
                  <span
                    key={i}
                    className={`h-[3px] flex-1 rounded-full transition-colors duration-300 ${i < filled ? "bg-[var(--signal)]" : "bg-white/10"}`}
                  />
                ))}
              </div>
              <span id={`${id}-digits`} className="sr-only">{tr.digitsHint(filled)}</span>
              {loading && (
                <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                  <span className="trk-scan absolute inset-y-0 w-1/3 bg-[linear-gradient(90deg,transparent,oklch(0.9_0.19_122/0.2),transparent)]" />
                </span>
              )}
            </div>
            <button
              type="submit"
              data-testid="track-submit"
              disabled={loading}
              className="btn-primary h-14 sm:h-auto sm:min-w-[11rem] px-8 rounded-2xl text-lg gap-2.5 disabled:opacity-70"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden /> : <Search className="w-5 h-5" aria-hidden />}
              {loading ? tr.searching : tr.button}
            </button>
          </div>

          <div id={`${id}-status`} aria-live="polite" className="min-h-[1.5rem] mt-4">
            {errorKey && (
              <p className="flex items-start gap-2 text-[15px] text-[oklch(0.86_0.09_40)]" data-testid="track-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                {tr.errors[errorKey]}
              </p>
            )}
          </div>
        </form>

        {state.status === "found" && <TrackResult key={state.shipment.trackingNumber} shipment={state.shipment} onReset={onReset} />}
      </div>
    </section>
  );
}
